import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';

import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  styled,
  useTheme,
  ThemeProvider,
  createTheme,
  darken,
} from '@mui/material/styles';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/PhoneIphone';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import ExportIcon from '@mui/icons-material/CloudDownloadOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import UndoIcon from '@mui/icons-material/Undo';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';

import Button from '../shared/Button';
import { CaseSteps } from '../cases';

import trainingIcon from '../../assets/training_icon_active.svg';
import paidIcon from '../../assets/paid_icon.svg';

import { utils } from '../../util';

import './modal.scss';

const DialogTitleRoot = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  background:
    theme.palette.mode === 'dark'
      ? darken(theme.palette.background.paper, 0.25)
      : '#2B2B2B',
  color: '#FFFFFF',
  textTransform: 'uppercase',
}));

const DialogTitleCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const DialogActions = styled(MuiDialogActions, {
  shouldForwardProp: (prop) => prop !== 'closeButton',
})(({ theme, closeButton }) => ({
  margin: 0,
  padding: theme.spacing(2),
  borderTop: '1px solid #f2f2f2',
  justifyContent: closeButton ? 'space-between' : 'flex-end',
}));

/* tooltip style from makeStyles */
const tooltipSx = (theme) => ({
  padding: theme.spacing(1),
  fontSize: '12px',
  whiteSpace: 'pre-line !important',
});

const TabPanel = ({ children, value, index, ...other }) => (
  <Typography
    component="div"
    role="tabpanel"
    hidden={value !== index}
    id={`full-width-tabpanel-${index}`}
    aria-labelledby={`full-width-tab-${index}`}
    {...other}
  >
    <Box sx={{ height: '100%' }} p={0}>
      {children}
    </Box>
  </Typography>
);

const a11yProps = (index) => ({
  id: `full-width-tab-${index}`,
  'aria-controls': `full-width-tabpanel-${index}`,
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Modal = (props) => {
  const {
    open,
    onClose,
    onSubmit,
    onTabChange,
    title,
    subtitle,
    caseId,
    closeButton,
    submitButton,
    children,
    disabled,
    loading,
    tabs,
    size,
    fullWidth,
    activeTab,
    isCase,
    autoSize,
    isAdmin,
    isGlobalAdmin,
    isSeniorEngineer,
    isDistributorAdmin,
    onDeleteClick,
    onPostponeClick,
    onRestoreClick,
    onExportClick,
    step,
    trainingMode,
    flow,
    withExport,
    exportMenuItems = [],
    isPaid,
    onNotifyUsersClick,
    adminModal,
    onRefreshClick,
    onUndoClick,
    onTBCClick,
    onAdminApproveDateClick,
    onRequestDateClick,
    onAdminApproveConsultationDateClick,
    onRequestConsultationDateClick,
    activeCase,
    onSettingsClick,
    canRequestDateChange,
    canApproveConsultationDateChange,
    canRequestConsultationDateChange,
  } = props;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const getFlowTheme = utils.getFlowsTheme(flow).main;

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const requestedSurgeryDate = activeCase?.requestedSurgeryDate?.newDate || activeCase?.requestedSurgeryDateNew;
  const requestedConsultationDate = activeCase?.requestedConsultationDate?.newDate || activeCase?.requestedConsultationDateNew;

  return (
    <Dialog
      onClose={onClose}
      TransitionComponent={Transition}
      open={open}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      maxWidth={size}
      scroll={autoSize ? 'body' : 'paper'}
    >
      <DialogTitleRoot disableTypography>
        {(isCase || subtitle) && (
          <div className="d-flex space-between">
            {subtitle && <div className="subtitle">{subtitle}</div>}
            <div className="d-flex" style={{ marginRight: 40, marginTop: -8 }}>
              {isCase && isGlobalAdmin && requestedSurgeryDate && (
                <Tooltip
                  title="Review Surgery Date Request"
                  placement="bottom"
                  enterTouchDelay={50}
                  leaveTouchDelay={3000}
                  sx={tooltipSx(theme)}
                >
                  <IconButton
                    edge="start"
                    color="secondary"
                    onClick={() => onAdminApproveDateClick(activeCase)}
                    sx={{ mr: 1 }}
                  >
                    <EventIcon />
                  </IconButton>
                </Tooltip>
              )}

              {isCase && canRequestDateChange && !requestedSurgeryDate && (
                <Tooltip
                  title="Request New Surgery Date"
                  placement="bottom"
                  enterTouchDelay={50}
                  leaveTouchDelay={3000}
                  sx={tooltipSx(theme)}
                >
                  <IconButton
                    edge="start"
                    color="secondary"
                    onClick={() =>
                      activeCase.date
                        ? onRequestDateClick(activeCase)
                        : onTBCClick(activeCase)
                    }
                    sx={{ mr: 1 }}
                  >
                    <EventIcon />
                  </IconButton>
                </Tooltip>
              )}

              {isCase && !!activeCase?.consultationDate && !!requestedConsultationDate && !!canApproveConsultationDateChange && (
                <Tooltip
                  title="Review Consultation Date Request"
                  placement="bottom"
                  enterTouchDelay={50}
                  leaveTouchDelay={3000}
                  sx={tooltipSx(theme)}
                >
                  <IconButton
                    edge="start"
                    color="secondary"
                    onClick={() => onAdminApproveConsultationDateClick(activeCase)}
                    sx={{ mr: 1 }}
                  >
                    <EventIcon />
                  </IconButton>
                </Tooltip>
              )}

              {isCase && !!activeCase?.consultationDate && !requestedConsultationDate && !!canRequestConsultationDateChange && (
                <Tooltip
                  title="Request New Consultation Date"
                  placement="bottom"
                  enterTouchDelay={50}
                  leaveTouchDelay={3000}
                  sx={tooltipSx(theme)}
                >
                  <IconButton
                    edge="start"
                    color="secondary"
                    onClick={() => onRequestConsultationDateClick(activeCase)}
                    sx={{ mr: 1 }}
                  >
                    <EventIcon />
                  </IconButton>
                </Tooltip>
              )}

              {adminModal && isGlobalAdmin && (
                <IconButton color="secondary" onClick={onSettingsClick}>
                  <SettingsIcon />
                </IconButton>
              )}

              {adminModal && (
                <IconButton color="secondary" onClick={onRefreshClick}>
                  <RefreshIcon />
                </IconButton>
              )}

              {(isGlobalAdmin || isSeniorEngineer) && step > 0 && (
                <IconButton color="secondary" onClick={onUndoClick}>
                  <UndoIcon />
                </IconButton>
              )}

              {isAdmin && step > 0 && (
                <IconButton color="secondary" onClick={onNotifyUsersClick}>
                  <PhoneIcon />
                </IconButton>
              )}

              {(isAdmin || isDistributorAdmin) && step > 0 && !activeCase.archived && (
                <IconButton color="secondary" onClick={onPostponeClick}>
                  <BlockIcon />
                </IconButton>
              )}

              {(isGlobalAdmin || isSeniorEngineer) && step > 0 && !activeCase.archived && (
                <IconButton color="secondary" onClick={onDeleteClick}>
                  <DeleteIcon />
                </IconButton>
              )}

              {isGlobalAdmin && step > 0 && !!activeCase.archived && (
                <IconButton color="secondary" onClick={onRestoreClick}>
                  <RestoreFromTrashIcon />
                </IconButton>
              )}

              {withExport && caseId && (
                <>
                  <IconButton color="secondary" onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <ExportIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={isOpen}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                      sx: { maxHeight: 260, width: 320, maxWidth: 'calc(100vw - 32px)', mt: 6 },
                    }}
                  >
                    <MenuItem onClick={() => { onExportClick(); setAnchorEl(null); }}>
                      Referral Document
                    </MenuItem>
                    {exportMenuItems.map((item) => (
                      <MenuItem
                        key={item.id || item.label}
                        onClick={() => setAnchorEl(null)}
                        sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
                      >
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="default-styled-link"
                          style={{
                            display: 'block',
                            width: '100%',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                          }}
                        >
                          {item.label}
                        </a>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </div>
          </div>
        )}

        <div className="d-flex">
          {title}
          {trainingMode && <img src={trainingIcon} alt="" style={{ width: 16, marginLeft: 8 }} />}
          {isPaid && <img src={paidIcon} alt="" style={{ width: 16, marginLeft: 8 }} />}
        </div>

        {onClose && (
          <DialogTitleCloseButton onClick={onClose}>
            <CloseIcon />
          </DialogTitleCloseButton>
        )}

        {isCase && <CaseSteps style={getFlowTheme} flow={flow} />}
      </DialogTitleRoot>

      <ThemeProvider
        theme={(base) =>
          createTheme({
            ...base,
            palette: {
              ...base.palette,
              primary: flow ? { ...utils.getFlowsTheme(flow) } : base.palette.primary,
            },
          })
        }
      >
        <DialogContent sx={{ height: '100%', padding: 0 }}>
          {tabs?.length ? (
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <AppBar
                position="static"
                color="default"
                elevation={0}
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? darken(theme.palette.background.paper, 0.06)
                      : theme.palette.background.paper,
                  borderBottom:
                    theme.palette.mode === 'dark'
                      ? `1px solid ${theme.palette.divider}`
                      : '1px solid #F2F2F2',
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, value) => onTabChange(value)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  sx={{
                    minHeight: 48,
                    '& .MuiTab-root': {
                      minHeight: 48,
                    },
                  }}
                >
                  {tabs.map((tab, index) => (
                    <Tab
                      key={index}
                      label={typeof tab === 'object' ? tab.label : tab}
                      disabled={tab.disabled}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>
              </AppBar>

              <SwipeableViews index={activeTab} onChangeIndex={onTabChange} style={{ flex: 1 }} disabled>
                {tabs.map((_, index) => (
                  <TabPanel key={index} value={activeTab} index={index}>
                    {children}
                  </TabPanel>
                ))}
              </SwipeableViews>
            </Box>
          ) : (
            children
          )}
        </DialogContent>
      </ThemeProvider>

      {(closeButton || submitButton) && (
        <DialogActions closeButton={closeButton}>
          {closeButton && <Button type="cancel" text={closeButton} onClick={onClose} />}
          {submitButton && (
            <Button
              text={submitButton}
              onClick={onSubmit}
              color="primary"
              disabled={disabled}
              loading={loading}
            />
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default Modal;
